/**
 * Carrier Invoices admin page (Settings → Benefits → Carrier Invoices).
 *
 * Three views in a single route, switched by query state:
 *   * List — every uploaded invoice with status + headline numbers,
 *     plus the upload modal.
 *   * Detail (when ?invoice=<id>) — the reconciliation report:
 *     summary tiles, line-by-line table, action buttons (Generate
 *     correction letter, Mark resolved).
 *   * Letter modal — opens over the detail view; shows the AI-drafted
 *     correction letter with copy / download actions.
 *
 * No charts — the report is text-first because the value is in the
 * specific dollars and names, not aggregate trends.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ClipboardCopy,
  Download,
  FileText,
  Loader2,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Upload,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import {
  generateLetter,
  getInvoiceReport,
  getReconciliationSummary,
  listInvoices,
  markInvoiceResolved,
  reconcileInvoice,
  uploadInvoice,
  type CarrierInvoice,
  type CarrierInvoiceReport,
  type ReconciliationSummary,
} from "@/api/benefitsReconciliation";

const DISCREPANCY_LABELS: Record<string, { label: string; tone: "danger" | "warning" | "info" }> = {
  ghost: { label: "Ghost employee", tone: "danger" },
  missing: { label: "Missing from invoice", tone: "warning" },
  tier_mismatch: { label: "Tier mismatch", tone: "warning" },
  rate_mismatch: { label: "Rate mismatch", tone: "warning" },
  unknown: { label: "Unmatched", tone: "info" },
};

function fmtMoney(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}


export function CarrierInvoicesPage() {
  const [search, setSearch] = useSearchParams();
  const invoiceId = search.get("invoice");
  if (invoiceId) return <ReportView invoiceId={invoiceId} onBack={() => setSearch({})} />;
  return <ListView onOpen={(id) => setSearch({ invoice: id })} />;
}


// ── List view ───────────────────────────────────────────────────────


function ListView({ onOpen }: { onOpen: (id: string) => void }) {
  const { addToast } = useToast();
  const [invoices, setInvoices] = useState<CarrierInvoice[] | null>(null);
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const reload = async () => {
    try {
      const [list, sum] = await Promise.all([
        listInvoices(),
        getReconciliationSummary(),
      ]);
      setInvoices(list);
      setSummary(sum);
    } catch {
      addToast("error", "Failed to load invoices");
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Carrier Invoices"
        subtitle="Reconciliation Agent — automatically catches carrier billing errors"
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Tile
          icon={<FileText className="h-4 w-4" />}
          label="Invoices uploaded"
          value={invoices?.length ?? "—"}
        />
        <Tile
          icon={<AlertTriangle className="h-4 w-4 text-warning" />}
          label="Open discrepancies"
          value={summary?.open_discrepancies.length ?? "—"}
          tone={
            summary && summary.open_discrepancies.length > 0
              ? "warning"
              : undefined
          }
        />
        <Tile
          icon={<TrendingDown className="h-4 w-4 text-success" />}
          label="YTD savings identified"
          value={
            summary
              ? fmtMoney(summary.ytd_savings_identified)
              : "—"
          }
          tone={
            summary && summary.ytd_savings_identified > 0
              ? "success"
              : undefined
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent invoices</CardTitle>
          <Button size="sm" onClick={() => setShowUpload(true)}>
            <Upload className="h-4 w-4" />
            Upload invoice
          </Button>
        </CardHeader>

        {!invoices ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No invoices uploaded yet. Upload your first carrier
            invoice to start catching billing errors.
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {invoices.map((inv) => {
              const tone =
                inv.status === "discrepancies_found"
                  ? "warning"
                  : inv.status === "reconciled"
                    ? "success"
                    : "info";
              return (
                <li key={inv.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(inv.id)}
                    className="w-full text-left py-3 hover:bg-surface-hover/40 -mx-2 px-2 rounded-md flex items-center justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-text-primary truncate">
                          {inv.carrier_name}
                        </span>
                        <span className="text-xs text-text-muted">
                          {fmtDate(inv.invoice_date)}
                        </span>
                        <Badge variant={tone}>
                          {inv.status.replace(/_/g, " ")}
                        </Badge>
                        {inv.discrepancy_count > 0 && (
                          <Badge variant="danger">
                            {inv.discrepancy_count} issue
                            {inv.discrepancy_count === 1 ? "" : "s"}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-text-muted mt-0.5">
                        Invoiced {fmtMoney(inv.invoice_total)}
                        {inv.expected_total > 0 && (
                          <> · Expected {fmtMoney(inv.expected_total)}</>
                        )}
                        {inv.discrepancy_total !== 0 && (
                          <>
                            {" "}
                            ·{" "}
                            <span
                              className={
                                inv.discrepancy_total > 0
                                  ? "text-warning"
                                  : "text-info"
                              }
                            >
                              {inv.discrepancy_total > 0 ? "+" : ""}
                              {fmtMoney(inv.discrepancy_total)}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <UploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        uploading={uploading}
        setUploading={setUploading}
        onUploaded={async () => {
          setShowUpload(false);
          await reload();
        }}
      />
    </motion.div>
  );
}


// ── Upload modal ────────────────────────────────────────────────────


interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploading: boolean;
  setUploading: (v: boolean) => void;
  onUploaded: () => void;
}

function UploadModal({
  isOpen,
  onClose,
  uploading,
  setUploading,
  onUploaded,
}: UploadModalProps) {
  const { addToast } = useToast();
  const [carrier, setCarrier] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [csvText, setCsvText] = useState("");
  const [pdfText, setPdfText] = useState("");
  const [autoReconcile, setAutoReconcile] = useState(true);
  const [mode, setMode] = useState<"csv" | "pdf">("csv");
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCarrier("");
      setInvoiceNumber("");
      setCsvText("");
      setPdfText("");
      setMode("csv");
    }
  }, [isOpen]);

  const handleFile = async (file: File) => {
    if (file.name.toLowerCase().endsWith(".csv")) {
      setMode("csv");
      const text = await file.text();
      setCsvText(text);
    } else {
      // For PDF / other, fall through to the paste-text mode.
      setMode("pdf");
      addToast(
        "info",
        "Paste the extracted text from your PDF below — we'll let "
          + "Claude parse the line items.",
      );
    }
  };

  const submit = async () => {
    if (!carrier.trim()) {
      addToast("error", "Carrier name is required");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadInvoice({
        carrier_name: carrier.trim(),
        invoice_date: invoiceDate,
        invoice_number: invoiceNumber.trim() || undefined,
        csv_text: mode === "csv" ? csvText : undefined,
        pdf_text: mode === "pdf" ? pdfText : undefined,
        auto_reconcile: autoReconcile,
      });
      if (res.warning) {
        addToast("warning", res.warning);
      } else {
        addToast("success", "Invoice uploaded and reconciled");
      }
      onUploaded();
    } catch {
      addToast("error", "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload carrier invoice" size="lg">
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Carrier"
            placeholder="Blue Cross Blue Shield"
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
          />
          <Input
            label="Invoice date"
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
          />
        </div>
        <Input
          label="Invoice number (optional)"
          placeholder="INV-2026-05"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
        />

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Source
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMode("csv")}
              className={`px-3 py-1 text-xs rounded-full border ${
                mode === "csv"
                  ? "bg-accent text-white border-accent"
                  : "border-border text-text-muted hover:bg-surface-hover"
              }`}
            >
              CSV file
            </button>
            <button
              type="button"
              onClick={() => setMode("pdf")}
              className={`px-3 py-1 text-xs rounded-full border ${
                mode === "pdf"
                  ? "bg-accent text-white border-accent"
                  : "border-border text-text-muted hover:bg-surface-hover"
              }`}
            >
              PDF text (Claude parses)
            </button>
          </div>
        </div>

        {mode === "csv" ? (
          <div>
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border cursor-pointer hover:bg-surface-hover">
              <Upload className="h-4 w-4" />
              <span className="text-sm text-text-muted">
                Choose CSV file
              </span>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
            </label>
            {csvText && (
              <p className="text-xs text-text-muted mt-1">
                {csvText.split("\n").length - 1} rows loaded.
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              PDF text (paste extracted text)
            </label>
            <textarea
              rows={8}
              value={pdfText}
              onChange={(e) => setPdfText(e.target.value)}
              placeholder="Paste the text content of your invoice PDF here. Claude will extract line items."
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoReconcile}
            onChange={(e) => setAutoReconcile(e.target.checked)}
            className="rounded border-border text-accent focus:ring-accent"
          />
          Auto-reconcile after upload
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
          <Button variant="ghost" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={submit} isLoading={uploading}>
            <Sparkles className="h-4 w-4" />
            Upload & reconcile
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ── Detail / report view ───────────────────────────────────────────


function ReportView({
  invoiceId,
  onBack,
}: {
  invoiceId: string;
  onBack: () => void;
}) {
  const { addToast } = useToast();
  const [report, setReport] = useState<CarrierInvoiceReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [showLetter, setShowLetter] = useState(false);

  const reload = async () => {
    try {
      setReport(await getInvoiceReport(invoiceId));
    } catch {
      addToast("error", "Failed to load invoice");
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const discrepancyLines = useMemo(
    () =>
      (report?.lines ?? []).filter(
        (l) => l.match_status === "discrepancy" || l.match_status === "unmatched",
      ),
    [report],
  );

  const handleReconcile = async () => {
    setBusy(true);
    try {
      const r = await reconcileInvoice(invoiceId);
      setReport(r);
      addToast("success", "Reconciliation complete");
    } catch {
      addToast("error", "Reconciliation failed");
    } finally {
      setBusy(false);
    }
  };

  const handleResolve = async () => {
    setBusy(true);
    try {
      await markInvoiceResolved(invoiceId);
      addToast("success", "Marked resolved");
      await reload();
    } catch {
      addToast("error", "Couldn't mark resolved");
    } finally {
      setBusy(false);
    }
  };

  if (!report) {
    return (
      <Card>
        <div className="py-10 text-center text-text-muted">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      </Card>
    );
  }

  const overText =
    report.discrepancy_total > 0
      ? `${fmtMoney(report.discrepancy_total)} OVER`
      : report.discrepancy_total < 0
        ? `${fmtMoney(Math.abs(report.discrepancy_total))} UNDER`
        : "no net discrepancy";

  return (
    <motion.div {...pageTransition}>
      <div className="mb-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All invoices
        </button>
      </div>

      <PageHeader
        title={`${report.carrier_name} — ${fmtDate(report.invoice_date)}`}
        subtitle={
          report.invoice_number
            ? `Invoice #${report.invoice_number}`
            : "Reconciliation report"
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Tile
          icon={<FileText className="h-4 w-4" />}
          label="Invoice total"
          value={fmtMoney(report.invoice_total)}
        />
        <Tile
          icon={<CheckCircle2 className="h-4 w-4 text-success" />}
          label="Expected total"
          value={fmtMoney(report.expected_total)}
        />
        <Tile
          icon={
            report.discrepancy_total > 0 ? (
              <TrendingUp className="h-4 w-4 text-warning" />
            ) : (
              <TrendingDown className="h-4 w-4 text-info" />
            )
          }
          label="Discrepancy"
          value={overText}
          tone={
            report.discrepancy_total !== 0 ? "warning" : undefined
          }
        />
        <Tile
          icon={<AlertTriangle className="h-4 w-4 text-warning" />}
          label="Issues found"
          value={report.discrepancy_count + report.unmatched_count}
        />
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />
              Discrepancies
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReconcile}
              isLoading={busy}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Re-run reconciliation
            </Button>
            {discrepancyLines.length > 0 && (
              <Button size="sm" onClick={() => setShowLetter(true)}>
                <FileText className="h-3.5 w-3.5" />
                Generate correction letter
              </Button>
            )}
            {report.status === "discrepancies_found" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResolve}
                disabled={busy}
              >
                Mark resolved
              </Button>
            )}
          </div>
        </CardHeader>

        {discrepancyLines.length === 0 ? (
          <div className="py-6 text-center text-sm">
            <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-success" />
            <p className="text-text-primary">
              No discrepancies found. Invoice matches Lenavio.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {discrepancyLines.map((l, idx) => {
              const meta =
                l.discrepancy_code && DISCREPANCY_LABELS[l.discrepancy_code]
                  ? DISCREPANCY_LABELS[l.discrepancy_code]
                  : { label: "Discrepancy", tone: "warning" as const };
              return (
                <li key={l.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-text-muted">
                          {idx + 1}.
                        </span>
                        <Badge variant={meta.tone}>{meta.label}</Badge>
                        <span className="font-medium text-text-primary truncate">
                          {l.employee_name || "(unmatched)"}
                        </span>
                        {l.plan_name && (
                          <span className="text-xs text-text-muted">
                            {l.plan_name}
                            {l.tier ? ` · ${l.tier}` : ""}
                          </span>
                        )}
                      </div>
                      {l.discrepancy_detail && (
                        <p className="text-xs text-text-muted mt-1">
                          {l.discrepancy_detail}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm text-text-primary tabular-nums">
                        {fmtMoney(l.premium_amount)}
                      </div>
                      {l.overbill_amount !== 0 && (
                        <div
                          className={`text-xs tabular-nums ${
                            l.overbill_amount > 0
                              ? "text-warning"
                              : "text-info"
                          }`}
                        >
                          {l.overbill_amount > 0 ? "+" : ""}
                          {fmtMoney(l.overbill_amount)}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All invoice lines ({report.lines.length})</CardTitle>
        </CardHeader>
        <ul className="divide-y divide-border/30 text-sm">
          {report.lines.map((l) => (
            <li key={l.id} className="py-2 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0 truncate">
                {l.matched_employee_id ? (
                  <Link
                    to={`/employees/${l.matched_employee_id}`}
                    className="text-text-primary hover:text-accent hover:underline"
                  >
                    {l.employee_name || "—"}
                  </Link>
                ) : (
                  <span className="text-text-primary">{l.employee_name || "—"}</span>
                )}
                {l.plan_name && (
                  <span className="text-xs text-text-muted ml-2">
                    {l.plan_name}
                    {l.tier ? ` · ${l.tier}` : ""}
                  </span>
                )}
              </div>
              <div className="tabular-nums text-text-primary">
                {fmtMoney(l.premium_amount)}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <LetterModal
        isOpen={showLetter}
        invoiceId={invoiceId}
        carrierName={report.carrier_name}
        onClose={() => setShowLetter(false)}
      />
    </motion.div>
  );
}


// ── Correction-letter modal ────────────────────────────────────────


function LetterModal({
  isOpen,
  invoiceId,
  carrierName,
  onClose,
}: {
  isOpen: boolean;
  invoiceId: string;
  carrierName: string;
  onClose: () => void;
}) {
  const { addToast } = useToast();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    generateLetter(invoiceId)
      .then((r) => setText(r.text))
      .catch(() => addToast("error", "Couldn't generate letter"))
      .finally(() => setLoading(false));
  }, [isOpen, invoiceId, addToast]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      addToast("success", "Copied to clipboard");
    } catch {
      addToast("error", "Couldn't copy");
    }
  };

  const download = () => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `correction-letter-${carrierName.replace(/\s+/g, "_")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Carrier correction letter — ${carrierName}`}
      size="xl"
    >
      <div className="space-y-3">
        {loading ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            <p className="mt-2 text-sm">Drafting…</p>
          </div>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={18}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary font-mono focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="secondary" onClick={download} disabled={!text}>
            <Download className="h-4 w-4" />
            Download .txt
          </Button>
          <Button onClick={copy} disabled={!text}>
            <ClipboardCopy className="h-4 w-4" />
            Copy to clipboard
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ── Tile primitive ──────────────────────────────────────────────────


function Tile({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone?: "warning" | "success" | "info";
}) {
  const toneClass =
    tone === "warning"
      ? "border-warning/30 bg-warning/5"
      : tone === "success"
        ? "border-success/30 bg-success/5"
        : tone === "info"
          ? "border-accent/30 bg-accent/5"
          : "border-border/40 bg-surface/60";
  return (
    <div className={`rounded-md border ${toneClass} p-3`}>
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-1 text-xl font-semibold text-text-primary">
        {value}
      </div>
    </div>
  );
}

export default CarrierInvoicesPage;
